'use client';

import { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { Slide, SlideProps } from '@mui/material';
import { appWithTheme } from '@scorenco/components';
import {
  ApolloService,
  AmplifyService,
  SentryService,
  CrispService,
  GoogleAnalyticsScript,
  useTranslation,
} from '@scorenco/core';
import { AppLayout, NextI18nService, AppPropsWithLayout } from '@scorenco/next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import i18nextHttpBackend from 'i18next-http-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import Head from 'next/head';
import { SnackbarProvider } from 'notistack';

import { ErrorFallback } from '../components/ErrorFallback';
import { usePurchases } from '../hooks/usePurchases';
import { CapacitorImagePickerProvider } from '../providers/CapacitorImagePickerProvider';
import { AppThemeProvider } from '../style';
import '../style/vendor.css';
import { T_KEYS } from '../translations';

// Les variables NEXT_PUBLIC_* sont inlinées au build : la branche inutile est
// éliminée du bundle.
const isStaticExport =
  process.env.NEXT_PUBLIC_TARGET_PLATFORM === 'ios' ||
  process.env.NEXT_PUBLIC_TARGET_PLATFORM === 'android';

if (isStaticExport) {
  AmplifyService.init({
    ssr: false,
  });
} else {
  AmplifyService.init();
}
// Définir un flag global par défaut pour iOS natif afin de gater le tracking
if (
  typeof window !== 'undefined' &&
  process.env.NEXT_PUBLIC_TARGET_PLATFORM === 'ios'
) {
  // false par défaut jusqu'à décision utilisateur
  (window as any).__trackingAuthorized = false;
}

CrispService.init();

// Build mobile (export statique) : pas d'i18n Next, la langue est détectée et
// chargée côté client. Build web (SSR) : i18n géré par Next.
if (isStaticExport) {
  const isBrowser = typeof window !== 'undefined';

  NextI18nService.init({
    i18n: {
      defaultLocale: 'fr',
      locales: ['fr', 'de'],
    },
    backend: {
      // En dev, pas de cache localStorage : les nouvelles clés s'affichent tout de suite
      backendOptions: [
        {
          expirationTime:
            process.env.NODE_ENV === 'development' ? 0 : 60 * 60 * 1000, // 1 hour
        },
      ],
      backends: isBrowser ? [LocalStorageBackend, i18nextHttpBackend] : [],
    },
    ns: ['snc-components', 'snc-core', 'common'],
    detection: {
      order: [
        'path',
        'querystring',
        'cookie',
        'localStorage',
        'sessionStorage',
        'navigator',
      ],
    },
    use: isBrowser ? [LanguageDetector, ChainedBackend] : [],
  });
} else {
  NextI18nService.init({
    i18n: {
      defaultLocale: 'fr',
      locales: ['fr', 'de'],
    },
    ns: ['snc-components', 'snc-core', 'common'],
  });
}

const StyledStyle = (props: SlideProps) => (
  <div
    css={(theme) => css`
      .notistack-MuiContent-success {
        background-color: ${theme.vars.palette.success.main};
        color: ${theme.vars.palette.success.contrastText};
      }
      .notistack-MuiContent-info {
        background-color: ${theme.vars.palette.info.main};
        color: ${theme.vars.palette.info.contrastText};
      }
      .notistack-MuiContent-error {
        background-color: ${theme.vars.palette.error.main};
        color: ${theme.vars.palette.error.contrastText};
      }
      .notistack-MuiContent-warning {
        background-color: ${theme.vars.palette.warning.main};
        color: ${theme.vars.palette.warning.contrastText};
      }
    `}
  >
    <Slide {...props} />
  </div>
);

const PremiumFlagSync = () => {
  const { isPremium, isLoading, error } = usePurchases();
  useEffect(() => {
    if (typeof window === 'undefined') return;

    (window as any).__isPremiumUser = Boolean(isPremium);
  }, [isPremium, isLoading, error]);
  return null;
};

const App = (appProps: AppPropsWithLayout) => {
  const { t } = useTranslation();
  const [trackingAuthorized, setTrackingAuthorized] = useState<boolean>(
    process.env.NEXT_PUBLIC_TARGET_PLATFORM !== 'ios'
  );

  const isIOSBuild = process.env.NEXT_PUBLIC_TARGET_PLATFORM === 'ios';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isIOSBuild) return;

    (async () => {
      try {
        const { AppTrackingTransparency } =
          await import('capacitor-plugin-app-tracking-transparency');
        const { status } = await AppTrackingTransparency.getStatus();
        if (status === 'notDetermined') {
          const { status: req } =
            await AppTrackingTransparency.requestPermission();
          (window as any).__trackingAuthorized = req === 'authorized';
          setTrackingAuthorized(req === 'authorized');
        } else {
          (window as any).__trackingAuthorized = status === 'authorized';
          setTrackingAuthorized(status === 'authorized');
        }
      } catch (_e) {
        // En cas d'erreur, considérer non autorisé
        (window as any).__trackingAuthorized = false;
        setTrackingAuthorized(false);
      }
    })();
  }, [isIOSBuild]);

  // La synchro du flag Premium est gérée par PremiumFlagSync à l'intérieur du provider AmplifyService

  const AppTree = (
    <>
      {isIOSBuild && trackingAuthorized && (
        <Head>
          <GoogleAnalyticsScript />
        </Head>
      )}
      <AppThemeProvider>
        <CapacitorImagePickerProvider>
          <SnackbarProvider
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            TransitionComponent={StyledStyle}
          >
            <SentryService
              Fallback={(props) => <ErrorFallback {...(props as any)} />}
            >
              <ApolloService>
                <AmplifyService>
                  <Head>
                    <title>{t(T_KEYS.DEFAULT_HTML_TITLE)}</title>
                  </Head>
                  <PremiumFlagSync />
                  <AppLayout {...appProps} />
                </AmplifyService>
              </ApolloService>
            </SentryService>
          </SnackbarProvider>
        </CapacitorImagePickerProvider>
      </AppThemeProvider>
    </>
  );

  return trackingAuthorized ? (
    <CrispService hideOnStart>{AppTree}</CrispService>
  ) : (
    AppTree
  );
};

if (
  typeof window !== 'undefined' &&
  process.env.NEXT_PUBLIC_TARGET_PLATFORM === 'web'
) {
  import('@ionic/pwa-elements/loader').then((pwaLoader) => {
    pwaLoader.defineCustomElements(window);
  });
}

export default appWithTheme(NextI18nService.appEnhancer(App));
